import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import { getObjectLabels, integrationEventHandlers } from '@paintscout/util/builder'
import Collapse from '../../components/Collapse'
import { useAdminIntegrationsConfig, useProcessEvent } from '../../hooks'
import type { StyleClasses } from '../../styles'
import classnames from 'classnames'
import { useSnackbar } from 'notistack'
import type { EventType, ProviderConfig } from 'paintscout'
import React, { useEffect, useMemo, useState } from 'react'
import type { DropdownSelectOption } from '../../'
import {
  Checkbox,
  DropdownSelect,
  EventSubscriptionDetails,
  EventSubscriptionType,
  FormControlLabel,
  FormSection,
  FormSectionTitle,
  Grid,
  Typography,
  useClientOptions,
  useUser
} from '../../'
import { ProviderFormSection } from './ProviderFormSection'
const useStyles = makeStyles<Theme, SalesforceProviderFormProps>(
  (theme) => ({
    root: {},
    formSection: {
      padding: theme.spacing(5, 0)
    },
    noPadding: {
      paddingBottom: 0
    },
    collapse: {
      width: '100%'
    },
    eventSubscription: {
      display: 'flex',
      alignItems: 'center'
    },
    eventSubscriptionText: {
      marginBottom: theme.spacing(1)
    }
  }),
  { name: 'SalesforceProviderForm' }
)

export interface SalesforceProviderFormProps {
  classes?: StyleClasses<typeof useStyles>
  providerConfig: ProviderConfig
  providerName: string
  onChange: (providerName: string, providerConfig: ProviderConfig, save?: boolean) => void
  isSettingsIntegration?: boolean
  isAdminIntegrations?: boolean
  companyId?: string
  userId?: string
}

export function SalesforceProviderForm(props: SalesforceProviderFormProps) {
  const {
    providerConfig,
    providerName,
    isSettingsIntegration = false,
    isAdminIntegrations = false,
    onChange,
    companyId,
    userId
  } = props
  const classes = useStyles(props)
  const { enabled, config } = providerConfig
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })
  const { enqueueSnackbar } = useSnackbar()
  const [loading, setLoading] = useState(false)

  const { adminIntegrationsConfig } = useAdminIntegrationsConfig()
  const { processEvent } = useProcessEvent()
  const declinedToRequired = !config?.declinedOpportunityStage?.value && config?.declinedUpdateStage
  const acceptedToRequired = !config?.acceptedOpportunityStage?.value && config?.acceptedUpdateStage
  const sentToRequired = config?.sentUpdateStage && !config?.sentOpportunityStage?.value && config?.sentUpdateStage
  const paidToRequired = config?.paidUpdateStage && !config?.paidOpportunityStage?.value && config?.paidUpdateStage
  const [opportunityStages, setOpportunityStages] = useState([])
  const [opportunityStatuses, setOpportunityStatuses] = useState([])
  const [quoteStages, setQuoteStages] = useState([])
  const [sfProducts, setSfProducts] = useState([])
  const [sfRecordTypes, setSfRecordTypes] = useState([])
  const [loadingStages, setLoadingStages] = useState(false)

  const { handleStageSelection, handleUpdateConfigCheckbox, handleEventChange } = useMemo(() => {
    return integrationEventHandlers({
      providerName,
      providerConfig,
      onChange
    })
  }, [providerName, providerConfig, onChange])

  useEffect(() => {
    async function load() {
      if (enabled) {
        setLoading(true)
        setLoadingStages(true)
        const configItemsRes = isAdminIntegrations
          ? await adminIntegrationsConfig({
              provider: providerName,
              type: 'get-config-items' as EventType,
              params: {
                companyId,
                userId
              }
            })
          : await processEvent({
              provider: providerName,
              type: 'get-config-items' as EventType,
              params: {}
            })

        if (configItemsRes) {
          const { result, error } = configItemsRes

          if (result?.result?.opportunityStages) {
            setOpportunityStages(result.result?.opportunityStages)
          } else if (error) {
            console.error(error)
            enqueueSnackbar('Error loading opportunity stages', { variant: 'error' })
          }

          if (result?.result?.opportunityStatuses) {
            setOpportunityStatuses(result.result?.opportunityStatuses)
          } else if (error) {
            console.error(error)
            enqueueSnackbar('Error loading opportunity statuses', { variant: 'error' })
          }

          if (result?.result?.quoteStages) {
            setQuoteStages(result.result?.quoteStages)
          } else if (error) {
            console.error(error)
            enqueueSnackbar('Error loading quote stages', { variant: 'error' })
          }

          if (result?.result?.products) {
            setSfProducts(result.result?.products)
          } else if (error) {
            console.error(error)
            enqueueSnackbar('Error loading products', { variant: 'error' })
          }

          if (result?.result?.opportunityRecordTypes) {
            setSfRecordTypes(result.result?.opportunityRecordTypes)
          } else if (error) {
            console.error(error)
            enqueueSnackbar('Error loading record types', { variant: 'error' })
          }
        }

        setLoading(false)
        setLoadingStages(false)
      }
    }
    setTimeout(() => {
      load()
    }, 3000)
  }, [enabled])

  const StageSelection = (props: { name: string; type?: 'opportunityStage' | 'opportunityStatus' | 'quote' }) => {
    const { name, type } = props
    let dropdownOptions = []
    if (type === 'quote') {
      dropdownOptions = quoteStages
    } else if (type === 'opportunityStage') {
      dropdownOptions = opportunityStages as DropdownSelectOption[]
    } else if (type === 'opportunityStatus') {
      dropdownOptions = opportunityStatuses as DropdownSelectOption[]
    }

    let selectPlaceholder = ''
    if (type === 'quote') {
      selectPlaceholder = 'Select Quote Stage...'
    } else if (type === 'opportunityStage') {
      selectPlaceholder = 'Select Opportunity Stage...'
    } else if (type === 'opportunityStatus') {
      selectPlaceholder = 'Select Opportunity Status...'
    }

    const value = dropdownOptions.find((option) => option.value === config[name]?.value)

    return (
      <div className={classes.stageSelection}>
        <DropdownSelect
          fullWidth
          name={name}
          disabled={loadingStages}
          loading={loadingStages}
          variant="single"
          placeholder={{
            selectPlaceholder
          }}
          value={value}
          options={dropdownOptions}
          onChange={(option) => handleStageSelection(name, option)}
        />
      </div>
    )
  }

  const DropdownItemSelection = (props: {
    name: string
    items: Array<{ value: number | string; label: string }>
    searchable?: boolean
  }) => {
    const { name, items, searchable } = props
    const selectedItem = items.filter((item) => item.value === providerConfig.config[name]?.value)[0] ?? null
    return (
      <DropdownSelect
        style={{ alignSelf: 'flex-end' }}
        name={name}
        searchable={searchable}
        disabled={loading}
        value={selectedItem}
        onChange={(option) => handleDropdownSelection(option, name)}
        options={items}
        className={classes.select}
        placeholder={{
          plural: 'Select Item...'
        }}
        variant={'single'}
      />
    )
  }
  const handleDropdownSelection = (option: any, name: string) => {
    onChange(providerName, {
      ...providerConfig,
      config: {
        ...providerConfig.config,
        [name]: option
      }
    })
  }
  const alternatePhoneNumberTypes = [
    { value: 'Mobile', label: 'Mobile' },
    { value: 'Home', label: 'Home' },
    { value: 'Assistant', label: 'Assistant' },
    { value: 'Other', label: 'Other' }
  ]

  const addressTypes = [
    { value: 'Mailing', label: 'Mailing' },
    { value: 'Other', label: 'Other' }
  ]

  const user = useUser()
  const isCorporate = (user?.isCorporate || user?.isSuperadmin) && !user.isMasquerading

  return (
    <>
      <FormSection hideDivider>
        <ProviderFormSection
          title="Connection Type"
          subTitle="Select the type of connection to use for this integration."
          rightContent={
            <Grid container spacing={1}>
              <Grid item xs={12} sm={12}>
                <div className={classes.eventSubscription}>
                  <FormControlLabel
                    classes={{ label: classes.formControlLabel }}
                    className={classnames({
                      [classes.required]: sentToRequired
                    })}
                    control={
                      <Checkbox
                        checked={config.useParentCompanyConnection}
                        name={'useParentCompanyConnection'}
                        onChange={handleUpdateConfigCheckbox('useParentCompanyConnection')}
                      />
                    }
                    label={`Use Parent Company Connection`}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={12}>
                <div className={classes.eventSubscription}>
                  <FormControlLabel
                    classes={{ label: classes.formControlLabel }}
                    className={classnames({
                      [classes.required]: sentToRequired
                    })}
                    control={
                      <Checkbox
                        checked={config.useParentCompanyConfig}
                        name={'useParentCompanyConfig'}
                        onChange={handleUpdateConfigCheckbox('useParentCompanyConfig')}
                      />
                    }
                    label={`Use Parent Company Configuration`}
                  />
                </div>
              </Grid>
            </Grid>
          }
        />
      </FormSection>
      {!config.useParentCompanyConfig ||
        (isCorporate && (
          <>
            <ProviderFormSection
              title={'Address Type'}
              subTitle={'Select which Salesforce address value to use as address in PaintScout.'}
              required={
                !providerConfig?.config.addressType?.value ||
                !addressTypes.filter((item) => item.value === providerConfig.config?.addressType?.value)[0]
              }
              rightContent={
                <div className={classes.itemSelection}>
                  <Typography className={classes.itemSelectText}>Use address:</Typography>
                  <DropdownItemSelection name={'addressType'} items={addressTypes} />
                </div>
              }
            />
            <ProviderFormSection
              title={'Alternate Phone Number Type'}
              subTitle={'Select which Salesforce phone number value to use as alternate phone number in PaintScout.'}
              required={
                !providerConfig?.config.alternatePhoneNumberType?.value ||
                !alternatePhoneNumberTypes.filter(
                  (item) => item.value === providerConfig.config?.alternatePhoneNumberType?.value
                )[0]
              }
              rightContent={
                <div className={classes.itemSelection}>
                  <Typography className={classes.itemSelectText}>Use phone number:</Typography>
                  <DropdownItemSelection name={'alternatePhoneNumberType'} items={alternatePhoneNumberTypes} />
                </div>
              }
            />
            <ProviderFormSection
              title={'Product Mappings'}
              subTitle={`Select which Salesforce products should be used when creating ${objectLabels.quote.plural}.`}
              rightContent={
                <div className={classes.itemSelection}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography className={classes.itemSelectText}>Labor Hours Product:</Typography>
                      <DropdownItemSelection name={'laborHoursProduct'} items={sfProducts} />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography className={classes.itemSelectText}>Subtotal Product:</Typography>
                      <DropdownItemSelection name={'subtotalProduct'} items={sfProducts} />
                    </Grid>
                  </Grid>
                </div>
              }
            />
            <ProviderFormSection
              title={'Record Type Mappings'}
              subTitle={`Select which Salesforce record type should be used when creating opportunities.`}
              rightContent={
                <div className={classes.itemSelection}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography className={classes.itemSelectText}>Record Type:</Typography>
                      <DropdownItemSelection name={'recordType'} items={sfRecordTypes} />
                    </Grid>
                  </Grid>
                </div>
              }
            />

            <FormSectionTitle title={'Create & Update Opportunities & Contacts'} variant={'h4'} />
            <Grid item xs={12}>
              <Typography variant="subtitle2">
                When a {objectLabels.quote.value} is createed, sent, accepted, or declined, you can update the
                opportunity in Salesforce
              </Typography>
              <Typography variant="subtitle2">
                We'll use the opportunity that was linked when the Salesforce contact was added.
              </Typography>
            </Grid>

            <FormSection className={classes.formSection}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={5}>
                  <div className={classes.eventSubscription}>
                    <EventSubscriptionType
                      isAdminIntegrations={isAdminIntegrations}
                      disableRadio={isSettingsIntegration}
                      name={'quote-created' as EventType}
                      event={providerConfig.events['quote-created']}
                      onChange={handleEventChange}
                      label={``}
                    />
                    <Typography variant={'body1'} className={classes.eventSubscriptionText}>
                      When {objectLabels.quote.plural} are <strong>created:</strong>
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={12} sm={7}>
                  <Collapse show={providerConfig.events['quote-created']?.enabled}>
                    <EventSubscriptionDetails
                      rows={[
                        <Grid container key={'draftUpdateStage'} spacing={3}>
                          <Grid container item xs={12}>
                            <Grid item xs={12}>
                              <FormControlLabel
                                classes={{ label: classes.formControlLabel }}
                                className={classnames({
                                  [classes.required]: sentToRequired
                                })}
                                control={
                                  <Checkbox
                                    checked={config.sentUpdateStage}
                                    name={'draftUpdateStage'}
                                    onChange={handleUpdateConfigCheckbox('draftUpdateStage')}
                                  />
                                }
                                label={`Update opportunity stage to: ${sentToRequired ? '*' : ''}`}
                              />
                            </Grid>
                            <Collapse show={config.draftUpdateStage}>
                              <Grid item xs={12}>
                                <StageSelection name={'draftOpportunityStage'} type="opportunityStage" />
                              </Grid>
                            </Collapse>
                          </Grid>
                          <Grid container item xs={12}>
                            <Grid item xs={12}>
                              <FormControlLabel
                                classes={{ label: classes.formControlLabel }}
                                className={classnames({
                                  [classes.required]: sentToRequired
                                })}
                                control={
                                  <Checkbox
                                    checked={config.draftUpdateQuoteStage}
                                    name={'draftUpdateQuoteStage'}
                                    onChange={handleUpdateConfigCheckbox('draftUpdateQuoteStage')}
                                  />
                                }
                                label={`Update ${objectLabels.quote.value.toLowerCase()} status to: ${sentToRequired ? '*' : ''}`}
                              />
                            </Grid>
                            <Collapse show={config.draftUpdateQuoteStage}>
                              <Grid item xs={12}>
                                <StageSelection name={'draftQuoteStage'} type="quote" />
                              </Grid>
                            </Collapse>
                          </Grid>
                        </Grid>
                      ]}
                    />
                  </Collapse>
                </Grid>
              </Grid>
            </FormSection>
            <FormSection className={classes.formSection}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={5}>
                  <div className={classes.eventSubscription}>
                    <EventSubscriptionType
                      isAdminIntegrations={isAdminIntegrations}
                      disableRadio={isSettingsIntegration}
                      name={'quote-sent' as EventType}
                      event={providerConfig.events['quote-sent']}
                      onChange={handleEventChange}
                      label={``}
                    />
                    <Typography variant={'body1'} className={classes.eventSubscriptionText}>
                      When {objectLabels.quote.plural} are <strong>sent:</strong>
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={12} sm={7}>
                  <Collapse show={providerConfig.events['quote-sent']?.enabled}>
                    <EventSubscriptionDetails
                      rows={[
                        <Grid container key={'sentUpdateStage'} spacing={3}>
                          <Grid container item xs={12}>
                            <Grid item xs={12}>
                              <FormControlLabel
                                classes={{ label: classes.formControlLabel }}
                                className={classnames({
                                  [classes.required]: sentToRequired
                                })}
                                control={
                                  <Checkbox
                                    checked={config.sentUpdateStage}
                                    name={'sentUpdateStage'}
                                    onChange={handleUpdateConfigCheckbox('sentUpdateStage')}
                                  />
                                }
                                label={`Update opportunity stage to: ${sentToRequired ? '*' : ''}`}
                              />
                            </Grid>
                            <Collapse show={config.sentUpdateStage}>
                              <Grid item xs={12}>
                                <StageSelection name={'sentOpportunityStage'} type="opportunityStage" />
                              </Grid>
                            </Collapse>
                          </Grid>
                          <Grid container item xs={12}>
                            <Grid item xs={12}>
                              <FormControlLabel
                                classes={{ label: classes.formControlLabel }}
                                className={classnames({
                                  [classes.required]: sentToRequired
                                })}
                                control={
                                  <Checkbox
                                    checked={config.sentUpdateOpportunityStatus}
                                    name={'sentUpdateOpportunityStatus'}
                                    onChange={handleUpdateConfigCheckbox('sentUpdateOpportunityStatus')}
                                  />
                                }
                                label={`Update opportunity status to: ${sentToRequired ? '*' : ''}`}
                              />
                            </Grid>
                            <Collapse show={config.sentUpdateOpportunityStatus}>
                              <Grid item xs={12}>
                                <StageSelection name={'sentOpportunityStatus'} type="opportunityStatus" />
                              </Grid>
                            </Collapse>
                          </Grid>
                          <Grid container item xs={12}>
                            <Grid item xs={12}>
                              <FormControlLabel
                                classes={{ label: classes.formControlLabel }}
                                className={classnames({
                                  [classes.required]: sentToRequired
                                })}
                                control={
                                  <Checkbox
                                    checked={config.sentUpdateQuoteStage}
                                    name={'sentUpdateQuoteStage'}
                                    onChange={handleUpdateConfigCheckbox('sentUpdateQuoteStage')}
                                  />
                                }
                                label={`Update ${objectLabels.quote.value.toLowerCase()} status to: ${sentToRequired ? '*' : ''}`}
                              />
                            </Grid>
                            <Collapse show={config.sentUpdateQuoteStage}>
                              <Grid item xs={12}>
                                <StageSelection name={'sentQuoteStage'} type="quote" />
                              </Grid>
                            </Collapse>
                          </Grid>
                        </Grid>
                      ]}
                    />
                  </Collapse>
                </Grid>
              </Grid>
            </FormSection>
            <FormSection className={classes.formSection}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={5}>
                  <div className={classes.eventSubscription}>
                    <EventSubscriptionType
                      isAdminIntegrations={isAdminIntegrations}
                      disableRadio={isSettingsIntegration}
                      name={'quote-accepted' as EventType}
                      event={providerConfig.events['quote-accepted']}
                      onChange={handleEventChange}
                      label={``}
                    />
                    <Typography variant={'body1'} className={classes.eventSubscriptionText}>
                      When {objectLabels.quote.plural} are <strong>accepted:</strong>
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={12} sm={7}>
                  <Collapse show={providerConfig.events['quote-accepted']?.enabled}>
                    <EventSubscriptionDetails
                      rows={[
                        <Grid key={'acceptedUpdateStage'} container spacing={3}>
                          <Grid container item>
                            <Grid item xs={12}>
                              <FormControlLabel
                                classes={{ label: classes.formControlLabel }}
                                className={classnames({
                                  [classes.required]: acceptedToRequired
                                })}
                                control={
                                  <Checkbox
                                    checked={config.acceptedUpdateStage}
                                    name={'acceptedUpdateStage'}
                                    onChange={handleUpdateConfigCheckbox('acceptedUpdateStage')}
                                  />
                                }
                                label={`Update opportunity stage to: ${acceptedToRequired ? '*' : ''}`}
                              />
                            </Grid>
                            <Collapse show={config.acceptedUpdateStage}>
                              <Grid item xs={12}>
                                <StageSelection name={'acceptedOpportunityStage'} type="opportunityStage" />
                              </Grid>
                            </Collapse>
                          </Grid>
                          <Grid container item>
                            <Grid item xs={12}>
                              <FormControlLabel
                                classes={{ label: classes.formControlLabel }}
                                className={classnames({
                                  [classes.required]: acceptedToRequired
                                })}
                                control={
                                  <Checkbox
                                    checked={config.acceptedUpdateOpportunityStatus}
                                    name={'acceptedUpdateOpportunityStatus'}
                                    onChange={handleUpdateConfigCheckbox('acceptedUpdateOpportunityStatus')}
                                  />
                                }
                                label={`Update opportunity status to: ${acceptedToRequired ? '*' : ''}`}
                              />
                            </Grid>
                            <Collapse show={config.acceptedUpdateOpportunityStatus}>
                              <Grid item xs={12}>
                                <StageSelection name={'acceptedOpportunityStatus'} type="opportunityStatus" />
                              </Grid>
                            </Collapse>
                          </Grid>
                          <Grid container item>
                            <Grid item xs={12}>
                              <FormControlLabel
                                classes={{ label: classes.formControlLabel }}
                                className={classnames({
                                  [classes.required]: acceptedToRequired
                                })}
                                control={
                                  <Checkbox
                                    checked={config.acceptedUpdateQuoteStage}
                                    name={'acceptedUpdateQuoteStage'}
                                    onChange={handleUpdateConfigCheckbox('acceptedUpdateQuoteStage')}
                                  />
                                }
                                label={`Update ${objectLabels.quote.value.toLowerCase()} status to: ${acceptedToRequired ? '*' : ''}`}
                              />
                            </Grid>
                            <Collapse show={config.acceptedUpdateQuoteStage}>
                              <Grid item xs={12}>
                                <StageSelection name={'acceptedQuoteStage'} type="quote" />
                              </Grid>
                            </Collapse>
                          </Grid>
                        </Grid>
                      ]}
                    />
                  </Collapse>
                </Grid>
              </Grid>
            </FormSection>
            <FormSection className={classes.formSection}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={5}>
                  <div className={classes.eventSubscription}>
                    <EventSubscriptionType
                      isAdminIntegrations={isAdminIntegrations}
                      disableRadio={isSettingsIntegration}
                      name={'quote-declined' as EventType}
                      event={providerConfig.events['quote-declined']}
                      onChange={handleEventChange}
                      label={``}
                    />
                    <Typography variant={'body1'} className={classes.eventSubscriptionText}>
                      When {objectLabels.quote.plural} are <strong>declined:</strong>
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={12} sm={7}>
                  <Collapse show={providerConfig.events['quote-declined']?.enabled}>
                    <EventSubscriptionDetails
                      rows={[
                        <Grid key={'declinedUpdateStage'} container spacing={3}>
                          <Grid container item>
                            <Grid item xs={12}>
                              <FormControlLabel
                                classes={{ label: classes.formControlLabel }}
                                className={classnames({
                                  [classes.required]: declinedToRequired
                                })}
                                control={
                                  <Checkbox
                                    checked={config.declinedUpdateStage}
                                    name={'declinedUpdateStage'}
                                    onChange={handleUpdateConfigCheckbox('declinedUpdateStage')}
                                  />
                                }
                                label={`Update opportunity stage to: ${declinedToRequired ? '*' : ''}`}
                              />
                            </Grid>
                            <Collapse show={config.declinedUpdateStage}>
                              <Grid item xs={12}>
                                <StageSelection name={'declinedOpportunityStage'} type="opportunityStage" />
                              </Grid>
                            </Collapse>
                          </Grid>
                          <Grid container item>
                            <Grid item xs={12}>
                              <FormControlLabel
                                classes={{ label: classes.formControlLabel }}
                                className={classnames({
                                  [classes.required]: declinedToRequired
                                })}
                                control={
                                  <Checkbox
                                    checked={config.declinedUpdateOpportunityStatus}
                                    name={'declinedUpdateOpportunityStatus'}
                                    onChange={handleUpdateConfigCheckbox('declinedUpdateOpportunityStatus')}
                                  />
                                }
                                label={`Update opportunity status to: ${declinedToRequired ? '*' : ''}`}
                              />
                            </Grid>
                            <Collapse show={config.declinedUpdateOpportunityStatus}>
                              <Grid item xs={12}>
                                <StageSelection name={'declinedOpportunityStatus'} type="opportunityStatus" />
                              </Grid>
                            </Collapse>
                          </Grid>
                          <Grid container item>
                            <Grid item xs={12}>
                              <FormControlLabel
                                classes={{ label: classes.formControlLabel }}
                                className={classnames({
                                  [classes.required]: declinedToRequired
                                })}
                                control={
                                  <Checkbox
                                    checked={config.declinedUpdateQuoteStage}
                                    name={'declinedUpdateQuoteStage'}
                                    onChange={handleUpdateConfigCheckbox('declinedUpdateQuoteStage')}
                                  />
                                }
                                label={`Update ${objectLabels.quote.value.toLowerCase()} status to: ${declinedToRequired ? '*' : ''}`}
                              />
                            </Grid>
                            <Collapse show={config.declinedUpdateQuoteStage}>
                              <Grid item xs={12}>
                                <StageSelection name={'declinedQuoteStage'} type="quote" />
                              </Grid>
                            </Collapse>
                          </Grid>
                        </Grid>
                      ]}
                    />
                  </Collapse>
                </Grid>
              </Grid>
            </FormSection>
            <FormSection className={classes.formSection}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={5}>
                  <div className={classes.eventSubscription}>
                    <EventSubscriptionType
                      isAdminIntegrations={isAdminIntegrations}
                      disableRadio={isSettingsIntegration}
                      name={'quote-owner-changed' as EventType}
                      event={providerConfig.events['quote-owner-changed']}
                      onChange={handleEventChange}
                      label={``}
                    />
                    <Typography variant={'body1'} className={classes.eventSubscriptionText}>
                      When {objectLabels.quote.value} <strong>owner is changed</strong>
                    </Typography>
                  </div>
                </Grid>
              </Grid>
            </FormSection>
            <FormSection className={classes.formSection}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={5}>
                  <div className={classes.eventSubscription}>
                    <EventSubscriptionType
                      isAdminIntegrations={isAdminIntegrations}
                      disableRadio={isSettingsIntegration}
                      name={'quote-paid' as EventType}
                      event={providerConfig.events['quote-paid']}
                      onChange={handleEventChange}
                      label={``}
                    />
                    <Typography variant={'body1'} className={classes.eventSubscriptionText}>
                      When Invoices are fully <strong>paid:</strong>
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={12} sm={7}>
                  <Collapse show={providerConfig.events['quote-paid']?.enabled}>
                    <EventSubscriptionDetails
                      rows={[
                        <Grid key={'paidUpdateStage'} container>
                          <Grid item xs={12}>
                            <FormControlLabel
                              classes={{ label: classes.formControlLabel }}
                              className={classnames({
                                [classes.required]: paidToRequired
                              })}
                              control={
                                <Checkbox
                                  checked={config.paidUpdateStage}
                                  name={'paidUpdateStage'}
                                  onChange={handleUpdateConfigCheckbox('paidUpdateStage')}
                                />
                              }
                              label={`Update opportunity stage to: ${paidToRequired ? '*' : ''}`}
                            />
                          </Grid>
                          <Collapse show={config.paidUpdateStage}>
                            <Grid item xs={12}>
                              <StageSelection name={'paidOpportunityStage'} type="opportunityStage" />
                            </Grid>
                          </Collapse>
                        </Grid>
                      ]}
                    />
                  </Collapse>
                </Grid>
              </Grid>
            </FormSection>
            <FormSection className={classes.formSection}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={12}>
                  <div className={classes.eventSubscription}>
                    <EventSubscriptionType
                      isAdminIntegrations={isAdminIntegrations}
                      disableRadio={isSettingsIntegration}
                      name={'contact-created' as EventType}
                      event={providerConfig.events['contact-created']}
                      onChange={handleEventChange}
                      label={``}
                    />
                    <Typography variant={'body1'} className={classes.eventSubscriptionText}>
                      Create Salesforce <strong>contacts</strong> when contacts are created in PaintScout
                    </Typography>
                  </div>
                </Grid>
              </Grid>
            </FormSection>
            <FormSection className={classes.formSection}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={12}>
                  <div className={classes.eventSubscription}>
                    <EventSubscriptionType
                      isAdminIntegrations={isAdminIntegrations}
                      disableRadio={isSettingsIntegration}
                      name={'contact-updated' as EventType}
                      event={providerConfig.events['contact-updated']}
                      onChange={handleEventChange}
                      label={``}
                    />
                    <Typography variant={'body1'} className={classes.eventSubscriptionText}>
                      Update Salesforce <strong>contacts</strong> when contacts are updated in PaintScout
                    </Typography>
                  </div>
                </Grid>
              </Grid>
            </FormSection>
          </>
        ))}
    </>
  )
}
